import React, { useEffect, useMemo, useState } from "react";
import "./../../../assets/css/purchase/form.css";
import moment from "moment";

// Components
import Breadcrumbs from "../../components/Breadcrumbs";
import Sidebar from "../../components/Sidebar";
import Pagination from "../../components/Footer/Pagination";

// Utils
import { formatCurrency, getAlert, getDataByQuery } from "../../../utils/SamuiUtils";
import { ExportMonthlyExpenseSummary } from "../../../utils/ExportUtils";

const MonthlyExpenseSummaryReport = () => {
  const [name, setName] = useState(null);

  // Criteria: select month (use a date but only month/year are used)
  const [criteria, setCriteria] = useState({ month: "" });

  // Data and paging
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [itemsPerPageDropdown, setItemsPerPageDropdown] = useState(10);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

  // Footer display range
  const startIndex = (currentPage - 1) * itemsPerPageDropdown + 1;
  const endIndex = Math.min(currentPage * itemsPerPageDropdown, data.length);

  // Change page size
  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPageDropdown(newItemsPerPage);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    setName("14. รายงานสรุปค่าใช้จ่ายประจำเดือน");
    setCriteria({ month: "" });
    setData([]);
    setCurrentPage(1);
  };

  const MONTHS = [
    { value: "01", label: "มกราคม" },
    { value: "02", label: "กุมภาพันธ์ " },
    { value: "03", label: "มีนาคม" },
    { value: "04", label: "เมษายน" },
    { value: "05", label: "พฤษภาคม" },
    { value: "06", label: "มิถุนายน" },
    { value: "07", label: "กรกฎาคม" },
    { value: "08", label: "สิงหาคม" },
    { value: "09", label: "กันยายน" },
    { value: "10", label: "ตุลาคม" },
    { value: "11", label: "พฤศจิกายน" },
    { value: "12", label: "ธันวาคม" },
  ];

  const handleSearch = async () => {
    try {
      const compId = window.localStorage.getItem("company") || 1;

      if (!criteria.month) {
        getAlert("FAILED", "กรุณาระบุเดือน");
        return;
      }

      const year = moment().format("YYYY");
      const month = String(parseInt(criteria.month, 10));

      const query = `
        SELECT  
            ISNULL(itg.Group_Name, '-') AS Group_Name,
            pd.Item_Name AS Item_Name,   
            SUM(pd.Item_Total) AS Total_Amount
            FROM PAY_H ph
            LEFT JOIN PAY_D pd ON pd.Pay_Id = ph.Pay_Id  
            LEFT JOIN Tb_Set_Item tsi ON pd.Item_Id = tsi.Item_Id 
            LEFT JOIN Tb_Set_ItmGroup itg ON tsi.Item_Group = itg.Group_Id  
            WHERE MONTH(ph.Pay_Date) = ${month}
              AND YEAR(ph.Pay_Date) = ${year}
              AND ph.Pay_Status = '2'
              AND ph.Comp_Id = ${compId}
            GROUP BY 
                pd.Item_Name,  
                itg.Group_Name
        ORDER BY  itg.Group_Name ASC, pd.Item_Name ASC
      `;

      const response = await getDataByQuery(query);
      if (response && Array.isArray(response)) {
        setData(response);
        setCurrentPage(1);
        if (response.length === 0) {
          getAlert("INFO", `ไม่มีข้อมูลค่าใช้จ่ายในเดือน ${MONTHS.find((m) => m.value === criteria.month)?.label || ""}`);
        }
      } else {
        setData([]);
        getAlert("FAILED", "เกิดข้อผิดพลาดในการดึงข้อมูล");
      }
    } catch (error) {
      console.error("Search Error:", error);
      getAlert("FAILED", error.response?.message || error.message);
    }
  };

  const handleExport = async () => {
    if (!data || data.length === 0) {
      return getAlert("INFO", "ไม่พบข้อมูลสำหรับพิมพ์รายงาน");
    }
    const year = moment().format("YYYY");
    const monthLabel = MONTHS.find((m) => m.value === criteria.month)?.label || "";
    await ExportMonthlyExpenseSummary(data, year, criteria.month, monthLabel);
  };

  const grandTotal = useMemo(() => {
    return data.reduce((sum, row) => sum + (Number(row.Total_Amount) || 0), 0);
  }, [data]);

  return (
    <>
      <Sidebar />
      <div className="main-panel">
        <div className="container">
          <div className="page-inner">
            <div className="page-header d-flex justify-content-between align-items-center">
              <Breadcrumbs
                page={name}
                items={[
                  { name: "รายงาน", url: "/reports" },
                  { name: name, url: "/monthly-expense-summary-report" },
                ]}
              />
            </div>

            <div className="row">
              {/* Criteria */}
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h4>เงื่อนไขการค้นหา</h4>
                  </div>
                  <div className="card-body">
                    <form className="row g-3">
                      <div className="col-md-6 d-flex align-items-center">
                        <label className="form-label me-2 mb-0">เดือน</label>
                        <select
                          className="form-select form-control input-spacing"
                          style={{ maxWidth: "240px" }}
                          value={criteria.month}
                          onChange={(e) => setCriteria({ month: e.target.value })}
                        >
                          <option value="">กรุณาระบุเดือน</option>
                          {MONTHS.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 d-flex align-items-end justify-content-end">
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white me-2"
                          style={{ backgroundColor: "#EF6C00", fontSize: "16px", minWidth: "150px" }}
                          onClick={handleSearch}
                        >
                          <i className="fas fa-search" /> ค้นหา
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{ backgroundColor: "#EF6C00", fontSize: "16px", minWidth: "150px" }}
                          onClick={initialize}
                        >
                          <i className="fas fa-undo" /> เริ่มใหม่
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="col-md-12 mt-4">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">ผลการค้นหา</h4>
                    <button
                      type="button"
                      className="btn btn-lg shadow text-white"
                      style={{ backgroundColor: "#EF6C00", fontSize: "16px", minWidth: "150px" }}
                      onClick={handleExport}
                    >
                      <i className="fas fa-file-excel" /> พิมพ์รายงาน
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive" style={{ maxHeight: "550px", overflowY: "auto" }}>
                      <table className="table table-striped table-hover">
                        <thead className="thead-dark">
                          <tr>
                            <th className="text-center">กลุ่มสินค้า</th>
                            <th className="text-center">รายละเอียด</th>
                            <th className="text-center">รวมจ่ายสุทธิ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems && currentItems.length > 0 ? (
                            <>
                              {currentItems.map((row, idx) => (
                                <tr key={`${row.Group_Name || '-'}-${row.Item_Name || idx}`}>
                                  <td className="text-center">{row.Group_Name || '-'}</td>
                                  <td className="text-left">{row.Item_Name || '-'}</td>
                                  <td className="text-end">{formatCurrency(Number(row.Total_Amount) || 0)}</td>
                                </tr>
                              ))}
                              {/* Summary row inside table */}
                              <tr
                                style={{
                                  backgroundColor: "#f8f9fa",
                                  fontWeight: "bold",
                                  borderTop: "2px solid #dee2e6",
                                }}
                              >
                                <td
                                  className="text-end"
                                  colSpan={2}
                                  style={{ fontWeight: "bold", fontSize: "14px" }}
                                >
                                  รวมค่าใช้จ่ายทั้งสิ้น
                                </td>
                                <td
                                  className="text-end"
                                  style={{ fontWeight: "bold", fontSize: "14px" }}
                                >
                                  {formatCurrency(grandTotal)}
                                </td>
                              </tr>
                            </>
                          ) : (
                            <tr>
                              <td colSpan={3}>
                                <center>
                                  <h5>ไม่พบข้อมูล</h5>
                                </center>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>


                   {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center p-3">
                      {/* ตัวเลือกจำนวนแถว */}
                      <div>
                        <select
                          value={itemsPerPageDropdown}
                          onChange={handleItemsPerPageChange}
                          className="form-select"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={30}>30</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>

                      {/* การแสดงจำนวนข้อมูล */}
                      <div>
                        <span>
                          แสดง {startIndex}-{endIndex} ของ{" "}
                          {data.length.toLocaleString()} รายการ
                        </span>
                      </div>

                      {/* Pagination */}
                      <Pagination
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        itemsPerPage={itemsPerPageDropdown}
                        dataMasterList={data}
                        handlePageChange={(pageNumber) => setCurrentPage(pageNumber)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonthlyExpenseSummaryReport;
